import React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Box,
  Alert,
} from "@mui/material";
import { Button as Btn } from "../../../components/atoms/Button";
import theme from "../../../theme";
import { FlexRow, Spacer } from "../../../components";
import { useSnackbar } from "../../../context/SnackbarContext";
import type { ColorsCodeType } from "../../../typings";
import { MdDescription, MdCheckCircle } from "react-icons/md";
import api from "../../../api/axiosInstance";

interface SignContractModalProps {
  open: boolean;
  onClose: () => void;
  scholarshipApplicationId: number;
  contractUrl: string;
  onRefresh: () => void;
  viewOnly?: boolean;
}

export default function SignContractModal({
  open,
  onClose,
  scholarshipApplicationId,
  contractUrl,
  onRefresh,
  viewOnly = false,
}: SignContractModalProps) {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string>("");
  const [iframeError, setIframeError] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(true);
  // The API host serves PDFs with X-Frame-Options: SAMEORIGIN, which blocks
  // cross-origin <iframe> embedding (Chrome reports this as "refused to
  // connect"). We fetch the PDF and hand the iframe a same-origin blob URL
  // instead, which bypasses that header without a backend change.
  const [iframeSrc, setIframeSrc] = useState<string>("");

  const handleSubmit = async () => {
    if (!accepted) {
      setError("Please accept the terms and conditions to proceed.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      await api.post("/scholarship-applications/sign-contract/", {
        scholarship_application_id: scholarshipApplicationId,
      });

      showSnackbar("Contract signed successfully!", "success");

      // Refresh the applications list
      onRefresh();

      // Reset form and close modal after successful submission
      setAccepted(false);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error("Error signing contract:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to sign contract. Please try again.";
      setError(errorMessage);
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setAccepted(false);
      setError("");
      setIframeError(false);
      setPdfLoading(true);
      onClose();
    }
  };

  // Reset states when modal opens
  useEffect(() => {
    if (open) {
      setIframeError(false);
      setPdfLoading(true);
      setIframeSrc("");
    }
  }, [open]);

  // Fetch the contract PDF as a blob and render it via a same-origin blob URL.
  // Falls through to the "Open in New Tab" UI if the fetch fails (usually a
  // CORS-blocked media path — in which case direct embedding wasn't going to
  // work either).
  useEffect(() => {
    if (!open || !contractUrl) return;

    let cancelled = false;
    let objectUrl: string | null = null;

    (async () => {
      try {
        const res = await fetch(contractUrl, { credentials: "omit" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.blob();
        if (cancelled) return;
        const pdfBlob = raw.type === "application/pdf"
          ? raw
          : new Blob([raw], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(pdfBlob);
        setIframeSrc(objectUrl);
      } catch {
        if (!cancelled) setIframeError(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, contractUrl]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "6px",
          maxWidth: "900px",
          width: "100%",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        style={{
          padding: "24px 24px 16px",
          fontSize: "24px",
          fontWeight: 600,
        }}
      >
        <FlexRow align="center">
          <MdDescription
            size={24}
            style={{
              marginRight: "10px",
              color: theme.palette.primary.main,
            }}
          />
          {viewOnly ? "View Contract" : "Sign Contract"}
        </FlexRow>
      </DialogTitle>
      <DialogContent
        style={{
          padding: "0px 24px 16px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            style={{
              color: theme.palette.colors.gray["500"],
              flex: 1,
            }}
          >
            {viewOnly
              ? "You can review the signed contract document below."
              : "Please review the contract document below carefully. You must accept the terms and conditions before signing."}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* Contract Preview */}
        <Box
          sx={{
            border: `1px solid ${theme.palette.colors.gray["300"]}`,
            borderRadius: "8px",
            overflow: "hidden",
            height: "500px",
            backgroundColor: theme.palette.colors.gray["50"],
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!iframeError ? (
            <>
              {pdfLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: theme.palette.colors.gray["50"],
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <CircularProgress size={40} />
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 2,
                        color: theme.palette.colors.gray["600"],
                      }}
                    >
                      Loading contract...
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 1,
                        display: "block",
                        color: theme.palette.colors.gray["500"],
                      }}
                    >
                      If the contract doesn't load,{" "}
                      <Box
                        component="span"
                        sx={{
                          color: theme.palette.primary.main,
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                        onClick={() => setIframeError(true)}
                      >
                        click here
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              )}
              {iframeSrc && (
                <iframe
                  src={iframeSrc}
                  title="Contract Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  onLoad={() => setPdfLoading(false)}
                  onError={() => setIframeError(true)}
                />
              )}
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2,
                p: 3,
                width: "100%",
                height: "100%",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.colors.gray["700"],
                  fontWeight: 600,
                }}
              >
                Unable to preview contract
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.colors.gray["500"],
                  textAlign: "center",
                  mb: 2,
                }}
              >
                The contract document cannot be displayed in this window due to
                security restrictions. Please click the button below to view it
                in a new tab.
              </Typography>
              <Btn
                onClick={() => window.open(contractUrl, "_blank")}
                buttonColor={
                  theme.palette.colors.gray["600"] as ColorsCodeType
                }
                textColor={theme.palette.colors.white["400"] as ColorsCodeType}
              >
                Open Contract in New Tab
              </Btn>
            </Box>
          )}
        </Box>

        {/* Acceptance Checkbox — hidden in view-only mode */}
        {!viewOnly && (
          <Box
            sx={{
              p: 2,
              border: `1px solid ${theme.palette.colors.gray["300"]}`,
              borderRadius: "8px",
              backgroundColor: theme.palette.colors.white["400"],
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={accepted}
                  onChange={(e) => {
                    setAccepted(e.target.checked);
                    if (error) setError("");
                  }}
                  color="primary"
                  sx={{
                    "& .MuiSvgIcon-root": {
                      fontSize: 24,
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.colors.gray["700"],
                    ml: 1,
                  }}
                >
                  I have read and understood the terms and conditions of this
                  contract. I agree to abide by all the terms stated in this
                  document.
                </Typography>
              }
              sx={{
                alignItems: "flex-start",
                m: 0,
              }}
            />
          </Box>
        )}

       
      </DialogContent>
      <FlexRow
        justify="flex-end"
        align="center"
        style={{ padding: "15px 24px 24px" }}
      >
        <Btn
          buttonColor={
            theme.palette.colors["black"]["600"] as ColorsCodeType
          }
          textColor={theme.palette.colors["white"]["400"] as ColorsCodeType}
          onClick={handleClose}
          disabled={loading}
        >
          {viewOnly ? "Close" : "Cancel"}
        </Btn>
        {!viewOnly && (
          <>
            <Spacer horizontal={1} />
            <Btn onClick={handleSubmit} disabled={loading || !accepted}>
              {loading ? (
                <>
                  <CircularProgress
                    size={16}
                    style={{
                      marginRight: 8,
                      color: theme.palette.colors.white[400] as string,
                    }}
                  />
                  Signing...
                </>
              ) : (
                <>
                  <MdCheckCircle
                    size={18}
                    style={{ marginRight: 8 }}
                  />
                  Sign Contract
                </>
              )}
            </Btn>
          </>
        )}
      </FlexRow>
    </Dialog>
  );
}

